import moment, { Moment } from "moment";
import { Method360, PrimaryTimeUtils } from "./primary-time.utils.ts";

export enum CouponFrequency {
    ANNUAL = 1,
    SEMIANNUAL = 2
}

export const FACE_VALUE_DEFAULT = 100.0;

export class PrimaryPricingUtils {

    /**
     * Calculate the price of a bond not traded at the coupon date.
     * 
     * Calculating the bond price involves discounting the future cash flows, which include interest payments and the principal 
     * repayment, to their present value using the required yield or discount rate. The bond price is the sum of the present 
     * values of all these cash flows.
     * 
     * @param faceValue The face value, or par value, is the amount the bond issuer agrees to repay the bondholder at the bond's maturity.
     * @param maturityDate The maturity date is the point when the bond's principal is due for repayment to the bondholder.
     * @param settlementDate The date the buyer acquires the bond.
     * @param coupon The coupon coupon is the interest coupon the bond issuer commits to paying on the bond's face value.
     * @param bondYield The yield is a measure of the return an investor anticipates earning if the bond is held to maturity. Expressed as an 
     *                  annual percentage, the yield is affected by the bond's purchase price, face value, coupon coupon, and the time until maturity.
     * @returns The price of the bound.
     * 
     * @remarks
     * If you are not familiar with bond pricing, I recommend you searching on te internet, there's a lot of information
     * that will help you understand what's going on.
     */
    public static calculateBondPrice(
        maturityDate: Moment, 
        settlementDate: Moment, 
        couponRate: number, 
        bondYield: number, 
        faceValue: number = FACE_VALUE_DEFAULT,
        frequency: CouponFrequency = CouponFrequency.SEMIANNUAL
    ): number {
        // If coupon rate is essentially equal to bond yield, return par value
        if (Math.abs(couponRate - bondYield) < 0.0001) return 100.0;

        // Determine the coupon payment periods
        const couponPeriodStart = PrimaryTimeUtils.getCouponPeriodStart(settlementDate, maturityDate);
        const couponPeriodEnd = moment(couponPeriodStart).add(
            frequency === CouponFrequency.ANNUAL ? 1 : 6, 
            frequency === CouponFrequency.ANNUAL ? "years" : "months"
        );

        const totalCouponPeriods = PrimaryTimeUtils.daysDifference360(
            couponPeriodEnd, 
            maturityDate, 
            Method360.US_NASD
        ) / (frequency === CouponFrequency.ANNUAL ? 360 : 180) + 1;

        // Calculate conversion factors
        const daysPerCouponPeriod = 360 / frequency;
        const daysSinceLastCouponPeriod = PrimaryTimeUtils.daysDifference360(
            couponPeriodStart, 
            settlementDate, 
            Method360.US_NASD
        );
        const periodYieldRate = bondYield / frequency;
        const periodCouponRate = couponRate / frequency;

        let bondPrice = 0.0;

        if (totalCouponPeriods > 1) {
            // Days since last coupon to next coupon period
            const daysToNextCoupon = PrimaryTimeUtils.daysDifference360(
                settlementDate, 
                couponPeriodEnd, 
                Method360.US_NASD
            );
            const periodsUntilMaturity = daysToNextCoupon / daysPerCouponPeriod;

            // Calculate present value of face value
            bondPrice += faceValue / Math.pow(periodYieldRate + 1, totalCouponPeriods - 1 + periodsUntilMaturity);
            
            // Calculate present value of coupon payments
            for (let periodIndex = 1; periodIndex <= totalCouponPeriods; periodIndex++) {
                bondPrice += (periodCouponRate * 100) / Math.pow(periodYieldRate + 1, periodsUntilMaturity + periodIndex - 1);
            }
            
            // Adjust for partial period
            bondPrice -= (periodCouponRate * daysSinceLastCouponPeriod * 100) / daysPerCouponPeriod;
        } else {
            // Special handling for single period
            const remainingDaysInPeriod = daysPerCouponPeriod - daysSinceLastCouponPeriod;
            const fullPeriodValue = periodCouponRate * 100 + faceValue;
            const discountFactor = (periodYieldRate * remainingDaysInPeriod) / daysPerCouponPeriod + 1;
            const partialPeriodAdjustment = (periodCouponRate * daysSinceLastCouponPeriod * 100) / daysPerCouponPeriod;
            
            bondPrice = fullPeriodValue / discountFactor - partialPeriodAdjustment;
        }

        return bondPrice;
    }


    /**
     * Calculates a bond's yield to maturity (YTM). The YTM is a measure of the total return an investor will receive 
     * from a bond if the bond is held until its maturity date.
     */
    public static calculateYieldToMaturity(
        maturityDate: Moment, 
        settlementDate: Moment, 
        couponRate: number, 
        currentPrice: number, 
        faceValue: number = FACE_VALUE_DEFAULT,
        frequency: CouponFrequency = CouponFrequency.SEMIANNUAL
    ) {
        
        // Determine the current coupon period
        const couponPeriodStart = PrimaryTimeUtils.getCouponPeriodStart(settlementDate, maturityDate);
        const couponPeriodEnd = moment(couponPeriodStart).add(6, "month");

        // Calculate total number of coupon periods
        const totalCouponPeriods = PrimaryTimeUtils.daysDifference360(couponPeriodEnd, maturityDate, Method360.US_NASD) / 180 + 1;
        if (totalCouponPeriods === 1) {
            // Special handling for single coupon period
            const periodCouponRate = couponRate / frequency;
            const daysInCouponPeriod = 360 / frequency;
            const daysSinceLastCoupon = PrimaryTimeUtils.daysDifference360(couponPeriodStart, settlementDate, Method360.US_NASD);
            const remainingDaysInPeriod = daysInCouponPeriod - daysSinceLastCoupon;

            const modifiedPrice = currentPrice / 100 + (daysSinceLastCoupon / daysInCouponPeriod) * periodCouponRate;

            let calculatedYield = faceValue / 100 + periodCouponRate - modifiedPrice;
            calculatedYield *= (daysInCouponPeriod * frequency) / (remainingDaysInPeriod * modifiedPrice);
            return calculatedYield
        }
        
        // Use binary search to find the optimal value
        const MAX_ITERATIONS = 200;
        let iterations = 0;

        let ytmUpperBound = -1;
        let ytmLowerBound = 1;
        let ytmCurrent = 0;

        while (iterations < MAX_ITERATIONS) {
			ytmCurrent = ytmLowerBound + (ytmUpperBound - ytmLowerBound) / 2;
            
            const bondPrice = PrimaryPricingUtils.calculateBondPrice(
                maturityDate, 
                settlementDate, 
                couponRate, 
                ytmCurrent
            )

            if (Math.abs(bondPrice - currentPrice) < 0.000001) {
                return ytmCurrent;
            }

			if (bondPrice > currentPrice) {
				ytmUpperBound = ytmCurrent;
			} else {
				ytmLowerBound = ytmCurrent;
			}

            iterations += 1;
		}
	
        return ytmCurrent;
    }

}
